import React, { useMemo } from 'react'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check-circle.svg'
import ViewportDropdown from 'shared/ui/ViewportDropdown/ViewportDropdown'
import s from './SelectDropdown.module.scss'

const normalizeOptions = options =>
	(options || []).map(option =>
		typeof option === 'string' ? { value: option, label: option } : option,
	)

export const SelectDropdown = ({
	value,
	onChange,
	options = [],
	disabled = false,
	placeholder = 'Select…',
	showActiveCheck = true,
	highlightActiveBackground = true,
	fullWidth = true,
	menuZIndex = 2100,
	minMenuWidth = 280,
	triggerClassName = '',
}) => {
	const dropdownOptions = useMemo(() => normalizeOptions(options), [options])
	const selectedLabel =
		dropdownOptions.find(option => option.value === value)?.label || placeholder
	const estimatedMenuHeight = Math.max(48, dropdownOptions.length * 42 + 8)
	const wrapClassName = fullWidth ? s.wrap : `${s.wrap} ${s.wrapInline}`.trim()
	const itemActiveClass = highlightActiveBackground ? s.itemActiveBg : s.itemActiveText

	return (
		<ViewportDropdown
			value={value}
			options={dropdownOptions}
			onChange={onChange}
			disabled={disabled}
			menuClassName={s.menu}
			estimatedMenuHeight={estimatedMenuHeight}
			minMenuWidth={minMenuWidth}
			menuZIndex={menuZIndex}
			renderTrigger={({ isOpen, disabled: isDisabled, toggle, triggerRef }) => (
				<div className={wrapClassName}>
					<button
						ref={triggerRef}
						type="button"
						className={`${s.trigger} ${isOpen ? s.open : ''} ${triggerClassName}`.trim()}
						onClick={toggle}
						disabled={isDisabled}
						aria-haspopup="listbox"
						aria-expanded={isOpen}
					>
						<span className={`${s.value} ${!value ? s.placeholder : ''}`.trim()}>
							{selectedLabel}
						</span>
						<span className={`${s.chevron} ${isOpen ? s.chevronOpen : ''}`.trim()} aria-hidden>
							<ArrowIcon />
						</span>
					</button>
				</div>
			)}
			renderOption={({ option, selected, onSelect }) => (
				<button
					key={option.value}
					type="button"
					className={`${s.item} ${selected ? itemActiveClass : ''}`.trim()}
					role="option"
					aria-selected={selected}
					onClick={onSelect}
				>
					<span className={s.itemText}>{option.label}</span>
					{showActiveCheck && selected ? (
						<span className={s.check} aria-hidden>
							<CheckIcon />
						</span>
					) : null}
				</button>
			)}
		/>
	)
}

export default SelectDropdown
